import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ForensicExplainModal from './ForensicExplainModal';
import '../styles/BeneishBreakdown.css';

/**
 * BeneishBreakdown
 * ─────────────────
 * Shown when the user clicks a Beneish M-Score cell.
 * Renders a waterfall-style contribution bar chart for all 8 components,
 * highlights top drivers, and shows plain-English hints.
 *
 * Props:
 *   year        – e.g. "2023"
 *   mScore      – numeric M-Score value
 *   zone        – "green" | "yellow" | "red"
 *   components  – array from the API (see backend beneish_values)
 *   topDrivers  – string[] of component names
 *   scoreExplain – worked-example payload for the M-Score header
 *   onClose     – callback to dismiss the panel
 */
export default function BeneishBreakdown({ year, mScore, zone, components, topDrivers, scoreExplain, onClose }) {
    const [expandedHint, setExpandedHint] = useState(null);
    const [explain, setExplain] = useState(null);

    if (!components || !components.length) return null;

    // Sort by absolute contribution delta (key drivers first), then by abs contribution
    const sorted = [...components].sort((a, b) => {
        const da = a.contributionDelta != null ? Math.abs(a.contributionDelta) : Math.abs(a.contribution);
        const db = b.contributionDelta != null ? Math.abs(b.contributionDelta) : Math.abs(b.contribution);
        return db - da;
    });

    // Scale bars: max absolute contribution across all components
    const maxAbs = Math.max(...components.map(c => Math.abs(c.contribution)), 0.01);

    const zoneLabel = { green: 'Low Risk', yellow: 'Caution', red: 'Elevated Warning' }[zone] || zone;
    const zoneBadgeClass = `bbd-zone-badge bbd-zone-${zone}`;

    const intercept = -4.84;

    return (
        <div className="bbd-overlay" onClick={onClose}>
            <div className="bbd-panel" onClick={e => e.stopPropagation()}>
                {/* ── Header ── */}
                <div className="bbd-header">
                    <div className="bbd-title-row">
                        <span className="bbd-title">
                            Beneish M-Score  ·  FY{year}
                            {scoreExplain && (
                                <button
                                    type="button"
                                    className="bbd-info"
                                    aria-label="How Beneish M-Score is calculated"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setExplain(scoreExplain);
                                    }}
                                >
                                    <FontAwesomeIcon icon="circle-info" />
                                </button>
                            )}
                        </span>
                        <button className="bbd-close" onClick={onClose} aria-label="Close">✕</button>
                    </div>
                    <div className="bbd-summary-row">
                        <span className="bbd-score">{mScore.toFixed(2)}</span>
                        <span className={zoneBadgeClass}>{zoneLabel}</span>
                    </div>
                    <p className="bbd-subtitle">
                        M = {intercept} (intercept)
                        {components.map(c => {
                            const sign = c.contribution >= 0 ? '+' : '';
                            return ` ${sign}${c.contribution.toFixed(3)} (${c.name})`;
                        }).join('')}
                        {' = '}<strong>{mScore.toFixed(2)}</strong>
                    </p>
                    {topDrivers && topDrivers.length > 0 && (
                        <p className="bbd-drivers-label">
                            Key drivers this year:&nbsp;
                            {topDrivers.map((d, i) => (
                                <span key={d} className="bbd-driver-chip">{d}{i < topDrivers.length - 1 ? '' : ''}</span>
                            ))}
                        </p>
                    )}
                </div>

                {/* ── Component bars ── */}
                <div className="bbd-body">
                    <div className="bbd-legend">
                        <span className="bbd-legend-item bbd-bar-positive">Pushes score up (worse)</span>
                        <span className="bbd-legend-item bbd-bar-negative">Pulls score down (better)</span>
                    </div>

                    {sorted.map(c => {
                        const isTopDriver = topDrivers?.includes(c.name);
                        const barWidth = Math.round((Math.abs(c.contribution) / maxAbs) * 100);
                        const isPositive = c.contribution >= 0;
                        const deltaDir = c.contributionDelta != null
                            ? (c.contributionDelta > 0.02 ? '▲' : c.contributionDelta < -0.02 ? '▼' : '→')
                            : null;
                        const deltaClass = c.contributionDelta != null
                            ? (c.contributionDelta > 0.02 ? 'bbd-delta-up' : c.contributionDelta < -0.02 ? 'bbd-delta-down' : 'bbd-delta-flat')
                            : '';

                        return (
                            <div
                                key={c.name}
                                className={`bbd-row ${isTopDriver ? 'bbd-row-driver' : ''}`}
                            >
                                {/* Label */}
                                <div className="bbd-row-label">
                                    <span className="bbd-comp-name">
                                        {c.name}
                                        {isTopDriver && <span className="bbd-key-badge">KEY</span>}
                                        {c.explain && (
                                            <button
                                                type="button"
                                                className="bbd-info"
                                                aria-label={`How ${c.name} is calculated`}
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    setExplain(c.explain);
                                                }}
                                            >
                                                <FontAwesomeIcon icon="circle-info" />
                                            </button>
                                        )}
                                    </span>
                                    <span className="bbd-comp-display">{c.displayName}</span>
                                </div>

                                {/* Bar */}
                                <div className="bbd-bar-cell">
                                    <div className="bbd-bar-track">
                                        <div
                                            className={`bbd-bar ${isPositive ? 'bbd-bar-positive' : 'bbd-bar-negative'}`}
                                            style={{ width: `${barWidth}%` }}
                                        />
                                    </div>
                                    <span className="bbd-contrib-val">
                                        {c.contribution >= 0 ? '+' : ''}{c.contribution.toFixed(3)}
                                    </span>
                                </div>

                                {/* Values */}
                                <div className="bbd-meta">
                                    <span className="bbd-val-chip">val = {c.value.toFixed(3)}</span>
                                    {deltaDir && (
                                        <span className={`bbd-delta ${deltaClass}`}>
                                            {deltaDir} {c.contributionDelta >= 0 ? '+' : ''}{c.contributionDelta.toFixed(3)} vs prior yr
                                        </span>
                                    )}
                                </div>

                                {/* Hint — expandable */}
                                {c.hint && (
                                    <button
                                        className="bbd-hint-btn"
                                        onClick={() => setExpandedHint(expandedHint === c.name ? null : c.name)}
                                        aria-expanded={expandedHint === c.name}
                                    >
                                        {expandedHint === c.name ? '▲ hide' : '▼ why?'}
                                    </button>
                                )}
                                {expandedHint === c.name && (
                                    <div className="bbd-hint-text">{c.hint}</div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* ── Footer ── */}
                <div className="bbd-footer">
                    Beneish M-Score is a statistical screening model, not proof of fraud.
                    Investigate further whenever the score is Yellow or Red.
                </div>
                <ForensicExplainModal explain={explain} onClose={() => setExplain(null)} />
            </div>
        </div>
    );
}
